import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment-timezone';
import * as React from 'react';
import { toast } from 'sonner';
import { useUpdateParticipantStatusMutation } from '../../../../../queries';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components-v2/Select';
import { meetingCancelled, meetingPending, meetingScheduled, navigation } from '../../../../styles/colors';
import { DetailsFlyout } from '../../../DetailsFlyout';
import { styleSheet as flyoutStyleSheet } from '../../../DetailsFlyout/styles';
import { LoadingSpinner } from '../../../LoadingSpinner';
import { ClipboardIcon } from '../../../svgs/icons/ClipboardIcon';
import { Badge } from '../../Badge';
import { MEETING_STATUS_CONFIG, getLocationDetail } from '../utils';
import { styleSheet } from './styles';

const INVITATION_STATUS_OPTIONS: { value: Api.InvitationStatus; label: string }[] = [
	{ value: Api.InvitationStatus.Pending, label: 'Pending' },
	{ value: Api.InvitationStatus.Confirmed, label: 'Confirmed' },
	{ value: Api.InvitationStatus.Declined, label: 'Declined' },
	{ value: Api.InvitationStatus.RequestedRescheduling, label: 'Requested Rescheduling' },
];

const isContactParticipant = (p: Api.IParticipant | Api.IContactParticipant): p is Api.IContactParticipant => {
	return (p as Api.IContactParticipant)._type === 'ContactParticipant';
};

/** Format start/end as a combined date/time string */
const formatDateRange = (startDate?: string, endDate?: string): string => {
	if (!startDate) return '-';
	const start = moment(startDate);
	if (!endDate) return start.format('MMM D, YYYY h:mm A');
	const end = moment(endDate);
	if (start.isSame(end, 'day')) {
		return `${start.format('MMM D, YYYY')} · ${start.format('h:mm')} - ${end.format('h:mm A')}`;
	}
	return `${start.format('MMM D, h:mm A')} - ${end.format('MMM D, h:mm A')}`;
};

/** Format timezone ID to abbreviation */
const formatTimezone = (timeZoneId?: string): string | null => {
	if (!timeZoneId) return null;
	try {
		return moment.tz(timeZoneId).zoneAbbr();
	} catch {
		return timeZoneId;
	}
};

const INVITATION_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
	[Api.InvitationStatus.Pending]: { label: 'Pending', color: meetingPending },
	[Api.InvitationStatus.Confirmed]: { label: 'Confirmed', color: meetingScheduled },
	[Api.InvitationStatus.Declined]: { label: 'Declined', color: meetingCancelled },
	[Api.InvitationStatus.RequestedRescheduling]: { label: 'Rescheduling', color: meetingPending },
};

const CopyableEmail = ({ email }: { email: string }) => {
	const copyToClipboard = async () => {
		try {
			await navigator.clipboard.writeText(email);
			toast.success('Copied to clipboard');
		} catch {
			toast.error('Unable to copy');
		}
	};

	return (
		<div className={css(styleSheet.emailRow)}>
			<span className={css(styleSheet.participantEmail)} title={email}>
				{email}
			</span>
			<button
				className={css(styleSheet.copyButton)}
				onClick={copyToClipboard}
				type='button'
				aria-label='Copy email to clipboard'
			>
				<ClipboardIcon fillColor={navigation} />
			</button>
		</div>
	);
};

export const MeetingDetailsFlyout = ({
	isOpen,
	setIsOpen,
	meeting,
	canUpdateStatus = true,
	onParticipantStatusUpdated,
}: {
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	meeting: Api.IScheduledMeeting;
	canUpdateStatus?: boolean;
	onParticipantStatusUpdated?: () => void;
}) => {
	const [updatingParticipantId, setUpdatingParticipantId] = React.useState<string | null>(null);
	const [pendingStatuses, setPendingStatuses] = React.useState<Record<string, Api.InvitationStatus>>({});
	const updatingParticipantIdRef = React.useRef<string | null>(null);
	const updateParticipantStatusMutation = useUpdateParticipantStatusMutation({
		onSuccess: () => {
			setUpdatingParticipantId(null);
			updatingParticipantIdRef.current = null;
			setPendingStatuses({});
			onParticipantStatusUpdated?.();
		},
		onError: () => {
			const failedId = updatingParticipantIdRef.current;
			setUpdatingParticipantId(null);
			updatingParticipantIdRef.current = null;
			setPendingStatuses(prev => {
				const next = { ...prev };
				if (failedId) delete next[failedId];
				return next;
			});
			toast.error('Failed to update participant status');
		},
	});

	const isCancelled = meeting.status === Api.MeetingStatus.Cancelled;
	const isStatusReadOnly = isCancelled || !canUpdateStatus;

	const getEffectiveStatus = (participant: Api.IParticipant): Api.InvitationStatus => {
		if (participant.id && pendingStatuses[participant.id]) return pendingStatuses[participant.id];
		if (participant.status && participant.status !== Api.InvitationStatus.Unknown) return participant.status;
		return Api.InvitationStatus.Pending;
	};

	const contactParticipants = React.useMemo(() => {
		return (meeting.participants ?? []).filter(isContactParticipant);
	}, [meeting.participants]);

	const teamParticipants = React.useMemo(() => {
		return (meeting.participants ?? []).filter(p => !isContactParticipant(p));
	}, [meeting.participants]);

	const locationDetail = getLocationDetail(meeting.location);
	const isVirtualLinkSafe = locationDetail != null && /^https?:\/\//i.test(locationDetail);

	const onStatusChange = (participant: Api.IParticipant, newStatus: Api.InvitationStatus) => {
		if (!meeting.id || !participant.id) return;
		updatingParticipantIdRef.current = participant.id;
		setUpdatingParticipantId(participant.id);
		setPendingStatuses(prev => ({ ...prev, [participant.id!]: newStatus }));
		updateParticipantStatusMutation.mutate({
			meetingId: meeting.id,
			participantId: participant.id,
			status: newStatus,
		});
	};

	return (
		<DetailsFlyout isOpen={isOpen} setIsOpen={setIsOpen} title='Meeting Details'>
			<div className={css(styleSheet.container)}>
				<section className={css(styleSheet.section)}>
					<h4 className={css(flyoutStyleSheet.subTitle)}>{meeting.title || 'Untitled Meeting'}</h4>
					<span className={css(styleSheet.dateSubtitle)}>
						{formatDateRange(meeting.startDate, meeting.endDate)}
						{` ${moment.tz(moment.tz.guess()).zoneAbbr()}`}
					</span>
				</section>

				<div className={css(flyoutStyleSheet.separator)} />

				<section className={css(styleSheet.detailsSection)}>
					<div className={css(styleSheet.detailRow)}>
						<span className={css(styleSheet.label)}>Status</span>
						{meeting.status && MEETING_STATUS_CONFIG[meeting.status] ? (
							<Badge
								text={MEETING_STATUS_CONFIG[meeting.status].label}
								foregroundColor={MEETING_STATUS_CONFIG[meeting.status].color}
							/>
						) : (
							<span className={css(styleSheet.value)}>-</span>
						)}
					</div>

					{meeting.meetingConfigName ? (
						<div className={css(styleSheet.detailRow)}>
							<span className={css(styleSheet.label)}>Meeting Name</span>
							<span className={css(styleSheet.value)}>{meeting.meetingConfigName}</span>
						</div>
					) : null}

					{meeting.scheduledDate ? (
						<div className={css(styleSheet.detailRow)}>
							<span className={css(styleSheet.label)}>Booked</span>
							<span className={css(styleSheet.value)}>{moment(meeting.scheduledDate).format('MMM D, YYYY')}</span>
						</div>
					) : null}

					{locationDetail ? (
						<div className={css(styleSheet.detailRow, styleSheet.detailRowTop)}>
							<span className={css(styleSheet.label)}>Location</span>
							<span className={css(styleSheet.locationValue)}>
								{meeting.location?._type === Api.MeetingLocation.Virtual && isVirtualLinkSafe ? (
									<a
										href={locationDetail}
										target='_blank'
										rel='noopener noreferrer'
										className={css(styleSheet.locationLink)}
									>
										Join Meeting
									</a>
								) : (
									locationDetail
								)}
							</span>
						</div>
					) : null}

					{meeting.creationContext?.source && meeting.creationContext.source !== Api.MeetingSource.Unknown ? (
						<div className={css(styleSheet.detailRow)}>
							<span className={css(styleSheet.label)}>Source</span>
							<span className={css(styleSheet.value)}>
								{meeting.creationContext.sourceIdentifier
									? `${meeting.creationContext.source} (${meeting.creationContext.sourceIdentifier})`
									: meeting.creationContext.source}
							</span>
						</div>
					) : null}
				</section>

				{contactParticipants.length > 0 ? (
					<>
						<div className={css(flyoutStyleSheet.separator)} />

						<section className={css(styleSheet.participantsSection)}>
							<h4 className={css(styleSheet.participantsSectionTitle)}>Contacts</h4>
							{contactParticipants.map(participant => {
								const name = `${participant.firstName ?? ''} ${participant.lastName ?? ''}`.trim() || 'Unknown';
								const isUpdating = updatingParticipantId === participant.id;
								return (
									<div className={css(styleSheet.participantRow)} key={`contact-participant-${participant.id}`}>
										<div className={css(styleSheet.participantInfo)}>
											<span className={css(styleSheet.participantName)}>{name}</span>
											{participant.emailAddress ? <CopyableEmail email={participant.emailAddress} /> : null}
											{participant.timeZoneId ? (
												<span className={css(styleSheet.participantTimezone)}>
													{formatTimezone(participant.timeZoneId)}
												</span>
											) : null}
											{participant.comments ? (
												<span className={css(styleSheet.participantComments)}>
													&ldquo;{participant.comments}&rdquo;
												</span>
											) : null}
										</div>
										<div className={css(styleSheet.statusSelect)}>
											{isStatusReadOnly ? (
												(() => {
													const config = INVITATION_STATUS_CONFIG[getEffectiveStatus(participant)];
													return config ? <Badge text={config.label} foregroundColor={config.color} /> : null;
												})()
											) : isUpdating ? (
												<LoadingSpinner type='tiny' />
											) : (
												<Select
													value={getEffectiveStatus(participant)}
													onValueChange={(value: string) => onStatusChange(participant, value as Api.InvitationStatus)}
													disabled={updateParticipantStatusMutation.isPending}
												>
													<SelectTrigger>
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														{INVITATION_STATUS_OPTIONS.map(option => (
															<SelectItem key={option.value} value={option.value}>
																{option.label}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											)}
										</div>
									</div>
								);
							})}
						</section>
					</>
				) : null}

				{teamParticipants.length > 0 ? (
					<>
						<div className={css(flyoutStyleSheet.separator)} />

						<section className={css(styleSheet.participantsSection)}>
							<h4 className={css(styleSheet.participantsSectionTitle)}>Users</h4>
							{teamParticipants.map(participant => {
								const name = participant.user
									? `${participant.user.firstName ?? ''} ${participant.user.lastName ?? ''}`.trim()
									: 'Unknown';
								const showStatus =
									!participant.isHost && participant.status && participant.status !== Api.InvitationStatus.Unknown;
								return (
									<div className={css(styleSheet.participantRow)} key={`team-participant-${participant.id}`}>
										<div className={css(styleSheet.participantInfo)}>
											<div className={css(styleSheet.participantNameRow)}>
												<span className={css(styleSheet.participantName)}>{name}</span>
												{participant.isHost ? <span className={css(styleSheet.hostBadge)}>Host</span> : null}
											</div>
											{participant.user?.email ? <CopyableEmail email={participant.user.email} /> : null}
										</div>
										{showStatus && participant.status && INVITATION_STATUS_CONFIG[participant.status] ? (
											<Badge
												text={INVITATION_STATUS_CONFIG[participant.status].label}
												foregroundColor={INVITATION_STATUS_CONFIG[participant.status].color}
											/>
										) : null}
									</div>
								);
							})}
						</section>
					</>
				) : null}
			</div>
		</DetailsFlyout>
	);
};
